import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, ShieldAlert, ToggleLeft } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { usePermissions } from "@/lib/permissions-context";
import { isForbiddenError } from "@/lib/queryClient";

type UserRole = "super_admin" | "tenant_admin" | "supervisor" | "agent";

type PermissionRule = {
  section: string;
  label: string;
};

type ProtectedFeature =
  | "knowledgeBaseManager"
  | "knowledgeBaseSearch"
  | "whatsapp"
  | "notifications"
  | "smsCampaigns"
  | "emailCampaigns";

type FeatureAccessRule = {
  featureName: string;
  featureStatusUrl: string;
  allowedRoles: readonly UserRole[];
  permission: PermissionRule | null;
  allowPlatformSuperAdmin: boolean;
};

type ProtectedFeatureRouteProps = {
  children: ReactNode;
  feature: ProtectedFeature;
};

type FeatureRouteState =
  | "loading"
  | "forbidden"
  | "featureDisabled"
  | "unavailable"
  | "allowed";

const FEATURE_ACCESS_RULES: Record<ProtectedFeature, FeatureAccessRule> = {
  knowledgeBaseManager: {
    featureName: "Knowledge Base",
    featureStatusUrl: "/api/kb/enabled",
    allowedRoles: ["super_admin", "tenant_admin", "supervisor"],
    permission: { section: "General", label: "Knowledge Base" },
    allowPlatformSuperAdmin: true,
  },
  knowledgeBaseSearch: {
    featureName: "Knowledge Base",
    featureStatusUrl: "/api/kb/enabled",
    allowedRoles: ["super_admin", "tenant_admin", "supervisor", "agent"],
    permission: { section: "General", label: "Knowledge Base" },
    allowPlatformSuperAdmin: true,
  },
  whatsapp: {
    featureName: "WhatsApp",
    featureStatusUrl: "/api/whatsapp/enabled",
    allowedRoles: ["super_admin", "tenant_admin"],
    permission: { section: "Conversations", label: "WhatsApp Settings" },
    allowPlatformSuperAdmin: false,
  },
  notifications: {
    featureName: "Notifications",
    featureStatusUrl: "/api/notifications/enabled",
    allowedRoles: ["super_admin", "tenant_admin", "supervisor"],
    permission: { section: "Settings", label: "Notifications" },
    allowPlatformSuperAdmin: false,
  },
  smsCampaigns: {
    featureName: "SMS Campaigns",
    featureStatusUrl: "/api/sms-campaigns/enabled",
    allowedRoles: ["super_admin", "tenant_admin", "supervisor"],
    permission: { section: "SMS Campaigns", label: "SMS Campaigns" },
    allowPlatformSuperAdmin: false,
  },
  emailCampaigns: {
    featureName: "Email Campaigns",
    featureStatusUrl: "/api/email-campaigns/enabled",
    allowedRoles: ["super_admin", "tenant_admin", "supervisor"],
    permission: null,
    allowPlatformSuperAdmin: false,
  },
};

function RouteLoadingState() {
  return (
    <div className="flex min-h-[400px] items-center justify-center" data-testid="feature-route-loading">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
    </div>
  );
}

function AccessDeniedState() {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center text-muted-foreground">
      <ShieldAlert className="mb-4 h-12 w-12 opacity-30" />
      <h1 className="mb-1 text-lg font-semibold text-foreground">Access denied</h1>
      <p className="text-sm" data-testid="feature-route-access-denied">
        You do not have permission to open this page.
      </p>
    </div>
  );
}

function FeatureDisabledState({ featureName }: { featureName: string }) {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center text-muted-foreground">
      <ToggleLeft className="mb-4 h-12 w-12 opacity-30" />
      <h1 className="mb-1 text-lg font-semibold text-foreground">Feature disabled</h1>
      <p className="text-sm" data-testid="feature-route-disabled">
        {featureName} is not enabled for this tenant.
      </p>
    </div>
  );
}

function FeatureStatusUnavailableState() {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center text-muted-foreground">
      <AlertTriangle className="mb-4 h-12 w-12 opacity-30" />
      <h1 className="mb-1 text-lg font-semibold text-foreground">Feature status unavailable</h1>
      <p className="text-sm" data-testid="feature-route-status-error">
        Feature access could not be verified. Please try again.
      </p>
    </div>
  );
}

export function ProtectedFeatureRoute({
  children,
  feature,
}: ProtectedFeatureRouteProps) {
  const {
    featureName,
    featureStatusUrl,
    allowedRoles,
    permission,
    allowPlatformSuperAdmin,
  } = FEATURE_ACCESS_RULES[feature];
  const { user, isImpersonating } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const effectiveRole: UserRole | null = user === null
    ? null
    : (isImpersonating ? "tenant_admin" : user.role);
  const permissionCheckPending = permission !== null
    && effectiveRole !== "super_admin"
    && permissionsLoading;
  const roleAllowed = effectiveRole !== null && allowedRoles.includes(effectiveRole);
  const permissionAllowed = permission === null
    || (effectiveRole !== null && hasPermission(permission.section, permission.label, effectiveRole));
  const accessAllowed = roleAllowed && permissionAllowed;
  const skipFeatureCheck = allowPlatformSuperAdmin
    && user !== null
    && user.role === "super_admin"
    && !isImpersonating;

  const featureStatus = useQuery<{ enabled: boolean }>({
    queryKey: [featureStatusUrl],
    enabled: user !== null && !permissionCheckPending && accessAllowed && !skipFeatureCheck,
    refetchOnMount: "always",
    staleTime: 0,
  });

  let routeState: FeatureRouteState;
  if (permissionCheckPending) {
    routeState = "loading";
  } else if (!accessAllowed) {
    routeState = "forbidden";
  } else if (skipFeatureCheck) {
    routeState = "allowed";
  } else if (featureStatus.isPending || featureStatus.isFetching) {
    routeState = "loading";
  } else if (isForbiddenError(featureStatus.error)) {
    routeState = "forbidden";
  } else if (featureStatus.isError) {
    routeState = "unavailable";
  } else if (featureStatus.data.enabled !== true) {
    routeState = "featureDisabled";
  } else {
    routeState = "allowed";
  }

  if (routeState === "loading") return <RouteLoadingState />;
  if (routeState === "forbidden") return <AccessDeniedState />;
  if (routeState === "featureDisabled") return <FeatureDisabledState featureName={featureName} />;
  if (routeState === "unavailable") return <FeatureStatusUnavailableState />;

  return <>{children}</>;
}
